import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2, Search, Building2 } from "lucide-react";
import { invoicesApi, buildingsApi, quotesApi } from "@/services/api";
import { fmtMoney, apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);
const blankLine = (p = {}) => ({ key: uid(), component: "", description: "", quantity: "1", cost: "", ...p });

function Seg({ value, options, onChange, testId }) {
  return (
    <div className="inline-flex rounded-lg bg-slate-100 p-1" data-testid={testId}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" onClick={() => onChange(o.value)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${value === o.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
          data-testid={`${testId}-${o.value}`}>{o.label}</button>
      ))}
    </div>
  );
}

export default function InvoiceCreate() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const qpBuilding = sp.get("building_id") || "";
  const qpQuote = sp.get("quote_id") || "";
  const qpType = sp.get("type");

  const [mode, setMode] = useState(qpBuilding ? "building" : "blank");
  const [invoiceType, setInvoiceType] = useState(qpType === "inspection" ? "inspection" : "maintenance");
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState(qpBuilding);
  const [buildingSearch, setBuildingSearch] = useState("");
  const [clientOverride, setClientOverride] = useState("");
  const [buildingOverride, setBuildingOverride] = useState("");
  const [lines, setLines] = useState([blankLine()]);
  const [notes, setNotes] = useState("");
  const [paymentTermsDays, setPaymentTermsDays] = useState("30");
  const [saving, setSaving] = useState(false);

  useEffect(() => { buildingsApi.getAll().then((r) => setBuildings(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!qpQuote) return;
    quotesApi.getOne(qpQuote).then((r) => {
      const q = r.data || {};
      const items = Array.isArray(q.items) ? q.items : [];
      if (items.length) setLines(items.map((it) => blankLine({ component: it.component || "", description: it.description || "", quantity: String(it.quantity ?? 1), cost: String(it.cost ?? "") })));
      if (q.building_id) { setSelectedBuildingId(q.building_id); setMode("building"); }
      if (q.quote_type === "inspection") setInvoiceType("inspection");
    }).catch(() => {});
  }, [qpQuote]);

  const filteredBuildings = useMemo(() => {
    const s = buildingSearch.trim().toLowerCase();
    return (s ? buildings.filter((b) => (b.name || "").toLowerCase().includes(s)) : buildings).slice(0, 30);
  }, [buildings, buildingSearch]);
  const selectedBuilding = useMemo(() => buildings.find((b) => b.id === selectedBuildingId) || null, [buildings, selectedBuildingId]);

  const totals = useMemo(() => {
    const subtotal = lines.reduce((s, ln) => {
      const q = parseFloat(ln.quantity || "0"); const c = parseFloat(ln.cost || "0");
      return isNaN(q) || isNaN(c) ? s : s + q * c;
    }, 0);
    const vat = +(subtotal * 0.2).toFixed(2);
    return { subtotal: +subtotal.toFixed(2), vat, total: +(subtotal + vat).toFixed(2) };
  }, [lines]);

  const setLine = (key, patch) => setLines((p) => p.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const delLine = (key) => setLines((p) => (p.length > 1 ? p.filter((l) => l.key !== key) : p));

  const save = async () => {
    if (mode === "building" && !selectedBuildingId) { toast.error("Pick a building, or switch to Blank invoice."); return; }
    if (mode === "blank" && !clientOverride.trim()) { toast.error("Type a client name for the blank invoice."); return; }
    const cleaned = lines.map((l) => ({
      component: l.component.trim() || "Charge",
      description: l.description.trim() || undefined,
      quantity: Math.max(1, parseFloat(l.quantity || "1") || 1),
      cost: Math.max(0, parseFloat(l.cost || "0") || 0),
    })).filter((l) => l.cost > 0);
    if (!cleaned.length) { toast.error("Add at least one priced line item."); return; }
    setSaving(true);
    try {
      const amount = cleaned.reduce((s, l) => s + l.quantity * l.cost, 0);
      const r = await invoicesApi.create({
        invoice_type: invoiceType,
        amount,
        vat: +(amount * 0.2).toFixed(2),
        total: +(amount * 1.2).toFixed(2),
        notes: notes.trim() || undefined,
        payment_terms_days: Math.max(0, parseInt(paymentTermsDays || "30", 10) || 30),
        building_id: mode === "building" ? selectedBuildingId : undefined,
        client_id: mode === "building" ? (selectedBuilding?.client_id || undefined) : undefined,
        items_snapshot: cleaned,
        client_name_override: mode === "blank" ? clientOverride.trim() : undefined,
        building_name_override: mode === "blank" && buildingOverride.trim() ? buildingOverride.trim() : undefined,
      });
      toast.success("Invoice created");
      navigate(`/invoices/${r.data.id}`);
    } catch (e) { toast.error(apiError(e, "Could not create invoice")); setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl pb-28">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <PageHeader title="New invoice" subtitle="Raise a maintenance or inspection invoice" testId="invoice-create-header" />

      <div className="space-y-5">
        <Card className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Label className="mb-1.5 block">Invoice for</Label>
              <Seg value={mode} onChange={setMode} testId="invoice-mode"
                options={[{ label: "A building", value: "building" }, { label: "Blank / new client", value: "blank" }]} />
            </div>
            <div>
              <Label className="mb-1.5 block">Type</Label>
              <Seg value={invoiceType} onChange={setInvoiceType} testId="invoice-type"
                options={[{ label: "Maintenance", value: "maintenance" }, { label: "Inspection", value: "inspection" }]} />
            </div>
          </div>
        </Card>

        {mode === "building" ? (
          <Card className="p-5">
            <Label className="mb-2 block">Building</Label>
            {selectedBuilding ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5" data-testid="invoice-selected-building">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-800"><Building2 className="h-4 w-4 text-primary" /> {selectedBuilding.name}</span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedBuildingId("")}>Change</Button>
              </div>
            ) : (
              <>
                <div className="relative mb-2">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input value={buildingSearch} onChange={(e) => setBuildingSearch(e.target.value)} placeholder="Search buildings…" className="pl-9" data-testid="invoice-building-search" />
                </div>
                <div className="max-h-56 space-y-1.5 overflow-auto">
                  {filteredBuildings.map((b) => (
                    <button key={b.id} onClick={() => setSelectedBuildingId(b.id)} className="flex w-full items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`invoice-building-${b.id}`}>
                      <span className="truncate text-slate-700">{b.name}</span>
                      {b.client_name ? <span className="shrink-0 text-xs text-slate-400">{b.client_name}</span> : null}
                    </button>
                  ))}
                </div>
              </>
            )}
          </Card>
        ) : (
          <Card className="space-y-4 p-5">
            <div>
              <Label>Client name *</Label>
              <Input value={clientOverride} onChange={(e) => setClientOverride(e.target.value)} placeholder="e.g. Acme Property Management" className="mt-1" data-testid="invoice-client-override" />
            </div>
            <div>
              <Label>Building / site (optional)</Label>
              <Input value={buildingOverride} onChange={(e) => setBuildingOverride(e.target.value)} placeholder="e.g. 12 Mulberry Ave" className="mt-1" data-testid="invoice-building-override" />
            </div>
          </Card>
        )}

        <Card className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <Label>Line items</Label>
            <Button variant="ghost" size="sm" onClick={() => setLines((p) => [...p, blankLine()])} data-testid="invoice-add-line"><Plus className="mr-1 h-3.5 w-3.5" /> Add line</Button>
          </div>
          <div className="space-y-3">
            {lines.map((ln, i) => (
              <div key={ln.key} className="rounded-lg border border-slate-200 p-3" data-testid={`invoice-line-${i}`}>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input value={ln.component} onChange={(e) => setLine(ln.key, { component: e.target.value })} placeholder="Item / charge" data-testid={`invoice-line-component-${i}`} />
                  <Input value={ln.description} onChange={(e) => setLine(ln.key, { description: e.target.value })} placeholder="Description (optional)" />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="w-24"><Label className="text-xs text-slate-500">Qty</Label><Input inputMode="decimal" value={ln.quantity} onChange={(e) => setLine(ln.key, { quantity: e.target.value })} data-testid={`invoice-line-qty-${i}`} /></div>
                  <div className="flex-1"><Label className="text-xs text-slate-500">Unit cost (£)</Label><Input inputMode="decimal" value={ln.cost} onChange={(e) => setLine(ln.key, { cost: e.target.value })} placeholder="0.00" data-testid={`invoice-line-cost-${i}`} /></div>
                  <div className="w-28 text-right"><Label className="text-xs text-slate-500">Line total</Label><div className="pt-2 text-sm font-semibold text-slate-800">{fmtMoney((parseFloat(ln.quantity || "0") || 0) * (parseFloat(ln.cost || "0") || 0))}</div></div>
                  <Button variant="ghost" size="icon" className="mt-4 text-red-500 hover:bg-red-50" onClick={() => delLine(ln.key)} data-testid={`invoice-line-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="space-y-4 p-5">
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes for this invoice…" className="mt-1" data-testid="invoice-notes" />
          </div>
          <div className="w-40">
            <Label>Payment terms (days)</Label>
            <Input inputMode="numeric" value={paymentTermsDays} onChange={(e) => setPaymentTermsDays(e.target.value)} className="mt-1" data-testid="invoice-payment-terms" />
          </div>
        </Card>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:pl-72">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            <span className="mr-3">Subtotal {fmtMoney(totals.subtotal)}</span>
            <span className="mr-3">VAT {fmtMoney(totals.vat)}</span>
            <span className="font-semibold text-slate-900">Total {fmtMoney(totals.total)}</span>
          </div>
          <Button onClick={save} disabled={saving} size="lg" data-testid="invoice-create-submit">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create invoice
          </Button>
        </div>
      </div>
    </div>
  );
}
